/**
 * Preview logic for the "Importuj parametry z rysunku ZE" panel.
 *
 * Extracted from TenderRoundSection so it can be unit-tested: the bug this guards against
 * is invisible-by-construction (a parameter that is silently dropped from the preview and
 * from the match count, yet still written into the preset).
 *
 * The rule throughout: a parameter the backend sent is a parameter the user sees. The
 * backend emits some keys ALWAYS, even at 0 — `ilosc_dz_prv_osc_dr_wewn`, its `_poza`
 * twin and `ilosc_dz_prv_tylko_likw_*` — precisely so a preset formula referencing them
 * always resolves. Hiding those zeros made "policzone i wyszło 0" indistinguishable from
 * "nikt tego nie policzył".
 */

/** Neighbour counts of manually marked internal roads. Zero here is only trustworthy once
 *  the roads have actually been swept — see `oscUntrusted`. */
export const OSC_KEYS = ["ilosc_dz_prv_osc_dr_wewn", "ilosc_dz_prv_osc_dr_wewn_poza"];

export interface ZERoadNeighborsInfo {
  roads: number;
  w_rysunku: number;
  poza_rysunkiem: number;
  roads_missing: string[];
}

export interface ImportRow {
  key: string;
  value: number;
  /** The selected preset declares this key, so inserting will write it. */
  matches: boolean;
  isZero: boolean;
  /** A neighbour count whose 0 may just mean "never swept". */
  untrusted: boolean;
}

/**
 * True when the drawing has marked internal roads whose neighbours were never discovered,
 * which makes the osc keys read 0 for a reason that has nothing to do with reality.
 * Undefined info (an older payload) is treated as trustworthy — we only warn on evidence.
 */
export function oscUntrusted(info?: ZERoadNeighborsInfo | null): boolean {
  return !!info && info.roads_missing.length > 0;
}

/**
 * Every parameter the backend sent, in display order: keys the preset actually uses first,
 * then zeros last within each group, then alphabetical. Nothing is filtered out — a value
 * of 0 is a result, not an absence.
 */
export function buildImportRows(
  params: Record<string, number>,
  presetKeys: Set<string>,
  untrusted: boolean,
): ImportRow[] {
  const rows: ImportRow[] = Object.entries(params).map(([key, value]) => ({
    key,
    value,
    matches: presetKeys.has(key),
    isZero: value === 0,
    untrusted: untrusted && OSC_KEYS.includes(key),
  }));
  return rows.sort(
    (a, b) =>
      Number(b.matches) - Number(a.matches) ||
      Number(a.isZero) - Number(b.isZero) ||
      a.key.localeCompare(b.key),
  );
}

/**
 * Counts driving the insert button's label and visibility. `matchCount` deliberately
 * includes zero-valued keys: a preset whose only drawing-backed parameters are the
 * always-emitted zeros must still be insertable, and used to render as
 * "Brak pasujących parametrów w wybranym presecie".
 */
export function summarizeImport(rows: ImportRow[]): {
  matchCount: number;
  zeroMatchCount: number;
  untrustedMatchCount: number;
} {
  const matched = rows.filter((r) => r.matches);
  return {
    matchCount: matched.length,
    zeroMatchCount: matched.filter((r) => r.isZero).length,
    untrustedMatchCount: matched.filter((r) => r.untrusted).length,
  };
}

/**
 * The values to merge into the preset parameter form. Zeros ARE written: the drawing is
 * the source of truth, and a real 0 has to be able to correct an inflated manual entry.
 * Only keys shown in the preview can be written, so nothing lands unseen.
 */
export function pickInsertable(rows: ImportRow[]): Record<string, string> {
  const out: Record<string, string> = {};
  for (const row of rows) {
    if (row.matches) out[row.key] = String(row.value);
  }
  return out;
}
